import { DataResult } from 'models/data';
import { Attribute, Measure, useModelService } from 'services/model/model.service';
import { ExpandableRow } from 'components/table/ExpandableRow';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { createExpanderCell, createMeasureCell, sortComparisonCell } from 'components/table/DataTableUtil';
import { IntlShape, useIntl } from 'react-intl';
import { ShareBar } from 'pages/insights-pro/common/reports/ShareBar';
import { DataTableContextProvider } from 'components/table/context/DataTableContextProvider';
import { DataTable } from 'components/table/DataTable';
import { DataTableExportConfigOptions } from 'components/table/DataTableExportUtil';
import { Service } from 'services/data.service';

interface MarketplaceChannelBreakdownTableRow extends ExpandableRow<MarketplaceChannelBreakdownTableRow> {
  marketplace: string;
  share: number;
  ordGmv: number;
  ordGmvCompare: number;
  ordNum: number;
  ordNumCompare: number;
  avgOrdGmv: number;
  avgOrdGmvCompare: number;
  retRate: number;
  retRateCompare: number;
  netSlsGmv: number;
  netSlsGmvCompare: number;
}

interface MarketplaceChannelBreakdownTableProps {
  response: DataResult;
  isComparisonActive: boolean;
}

const columnHelper = createColumnHelper<MarketplaceChannelBreakdownTableRow>();
const buildColumns = (chnMarketplace: Attribute, measures: Measure[], intl: IntlShape, comparisonActive: boolean) => [
  columnHelper.accessor('marketplace', {
    header: () => chnMarketplace.name,
    cell: context => createExpanderCell(context, <>{context.getValue()}</>, true),
  }),
  columnHelper.accessor('share', {
    header: () => intl.formatMessage({ id: 'insights.report.channelShare' }),
    cell: context => (
      <ShareBar width={context.getValue() * 100} color={context.row.getCanExpand() ? '#314968' : '#5CBBC7'} />
    ),
  }),
  columnHelper.accessor(row => ({ value: row.ordGmv, compareValue: row.ordGmvCompare }), {
    id: measures[0].code,
    header: () => measures[0].metric.name,
    cell: context => createMeasureCell(context, measures[0], false, comparisonActive),
    sortingFn: sortComparisonCell,
  }),
  columnHelper.accessor(row => ({ value: row.ordNum, compareValue: row.ordNumCompare }), {
    id: measures[1].code,
    header: () => measures[1].metric.name,
    cell: context => createMeasureCell(context, measures[1], false, comparisonActive),
    sortingFn: sortComparisonCell,
  }),
  columnHelper.accessor(row => ({ value: row.avgOrdGmv, compareValue: row.avgOrdGmvCompare }), {
    id: measures[2].code,
    header: () => measures[2].metric.name,
    cell: context => createMeasureCell(context, measures[2], false, comparisonActive),
    sortingFn: sortComparisonCell,
  }),
  columnHelper.accessor(row => ({ value: row.retRate, compareValue: row.retRateCompare }), {
    id: measures[3].code,
    header: () => measures[3].metric.name,
    cell: context => createMeasureCell(context, measures[3], false, comparisonActive),
    sortingFn: sortComparisonCell,
  }),
  columnHelper.accessor(row => ({ value: row.netSlsGmv, compareValue: row.netSlsGmvCompare }), {
    id: measures[4].code,
    header: () => measures[4].metric.name,
    cell: context => createMeasureCell(context, measures[4], false, comparisonActive),
    sortingFn: sortComparisonCell,
  }),
];

export function MarketplaceChannelBreakdownTable({
  response,
  isComparisonActive,
}: MarketplaceChannelBreakdownTableProps) {
  const { getAttributes, getMeasures } = useModelService();
  const [chnMarketplace, chnName] = getAttributes(['chnMarketplace', 'chnName']);
  const measures = getMeasures(['ordGmvVal', 'ordNum', 'avgOrdGmvVal', 'retQtyRateExpOD', 'netSlsGmvValExtExpOD']);
  const [ordGmv, ordNum, avgOrdGmv, retRate, netSlsGmv] = measures;
  const intl = useIntl();

  const rows = response.sets.marketplaces.map(entry => {
    const marketplaceRow: MarketplaceChannelBreakdownTableRow = {
      marketplace: chnMarketplace.getValue(entry.attributes),
      share: ordGmv.getValue(entry.values) / (ordGmv.getValue(response.values) ?? ordGmv.getValue(entry.values)),
      ordGmv: ordGmv.getValue(entry.values),
      ordGmvCompare: ordGmv.getValue(entry.compareValues),
      ordNum: ordNum.getValue(entry.values),
      ordNumCompare: ordNum.getValue(entry.compareValues),
      avgOrdGmv: avgOrdGmv.getValue(entry.values),
      avgOrdGmvCompare: avgOrdGmv.getValue(entry.compareValues),
      retRate: retRate.getValue(entry.values),
      retRateCompare: retRate.getValue(entry.compareValues),
      netSlsGmv: netSlsGmv.getValue(entry.values),
      netSlsGmvCompare: netSlsGmv.getValue(entry.compareValues),
      subRows: [],
    };

    marketplaceRow.subRows = entry.sets.channels.map(subRow => {
      const channelRow: MarketplaceChannelBreakdownTableRow = {
        marketplace: chnName.getValue(subRow.attributes),
        share: ordGmv.getValue(subRow.values) / (ordGmv.getValue(entry.values) ?? ordGmv.getValue(subRow.values)),
        ordGmv: ordGmv.getValue(subRow.values),
        ordGmvCompare: ordGmv.getValue(subRow.compareValues),
        ordNum: ordNum.getValue(subRow.values),
        ordNumCompare: ordNum.getValue(subRow.compareValues),
        avgOrdGmv: avgOrdGmv.getValue(subRow.values),
        avgOrdGmvCompare: avgOrdGmv.getValue(subRow.compareValues),
        retRate: retRate.getValue(subRow.values),
        retRateCompare: retRate.getValue(subRow.compareValues),
        netSlsGmv: netSlsGmv.getValue(subRow.values),
        netSlsGmvCompare: netSlsGmv.getValue(subRow.compareValues),
        subRows: [],
      };

      return channelRow;
    });

    return marketplaceRow;
  });

  const columns = buildColumns(
    chnMarketplace,
    measures,
    intl,
    isComparisonActive
  ) as ColumnDef<MarketplaceChannelBreakdownTableRow>[];

  const exportOptions: DataTableExportConfigOptions = {
    reportDownloadProps: {
      service: Service.Marketplaces,
      measuresGroup: 'channel-breakdown',
      tableName: 'Marketplace Breakdown',
      isProReport: true,
    },
  };

  return (
    <DataTableContextProvider>
      <DataTable
        titleId="insights.marketplace.reports.channel_breakdown.description"
        rows={rows}
        columns={columns}
        defaultSortingState={[{ id: measures[0].code, desc: true }]}
        exportConfigOptions={exportOptions}
      />
    </DataTableContextProvider>
  );
}
